import React, { useEffect, useState, useCallback } from "react";
import Table from "@mui/material/Table";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableContainer from "@mui/material/TableContainer";
import LinearProgress from "@mui/material/LinearProgress";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import FormControlLabel from "@mui/material/FormControlLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import TablePagination from "@mui/material/TablePagination";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { SIZES, ALIGN, HTML_TAGS } from "../../common/constants/commonConstants.js";

import {
  fetchReminders,
  createReminder,
  updateReminder,
  deleteReminder,
} from "../../services/reminderModule/reminderServices.js";
import { getcampaigns } from "../../services/api";
import apiClient from "../../services/apiInstance.js";

// ── Inline constants (page-local) ────────────────────────────────────────────
// Table columns for the list view.
const REMINDER_COLUMNS = [
  { id: "Name",     label: "Name" },
  { id: "Channel",  label: "Channel" },
  { id: "Trigger",  label: "Trigger" },
  { id: "Dpd",      label: "DPD" },
  { id: "Campaign", label: "Campaign" },
  { id: "Enabled",  label: "Enabled" },
  { id: "Actions",  label: "Actions" },
];

const CHANNEL_OPTIONS = [
  { value: "texts",  label: "Texts" },
  { value: "emails", label: "Emails" },
];

const TRIGGER_TYPE_OPTIONS = [
  { value: "lapse",    label: "Time lapse (after outbound)" },
  { value: "specific", label: "Specific date & time" },
];

const LAPSE_UNIT_OPTIONS = [
  { value: "hours", label: "Hours" },
  { value: "days",  label: "Days" },
];

// DPD buckets — exactly the platform's standard buckets. "Any" == none selected.
const DPD_BUCKET_OPTIONS = [
  "1-9", "10-19", "20-29", "30-39", "40-49", "50-59", "60-89", "90+",
];

// PST/EST UI labels mapped to IANA zones (DST-aware) for 'specific' reminders.
const TIMEZONE_OPTIONS = [
  { value: "America/Los_Angeles", label: "Pacific (PST/PDT)" },
  { value: "America/Denver",      label: "Mountain (MST/MDT)" },
  { value: "America/Chicago",     label: "Central (CST/CDT)" },
  { value: "America/New_York",    label: "Eastern (EST/EDT)" },
];

// Campaign filter option sets (mirror the campaign list pages).
const CATEGORY_OPTIONS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
  "Other",
];
const LANGUAGE_OPTIONS = ["English", "Spanish"];
const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];
const todayCategory = WEEKDAYS[new Date().getDay()]; // default Category filter = current day

// Quiet-hours window — must match processreminders.js / processTextsAdhoc.js.
const QUIET_START_HR = 8;   // 08:00 Pacific inclusive
const QUIET_END_HR   = 18;  // 18:00 Pacific exclusive

// Default blank form values for the create dialog.
// campaignId is REQUIRED — the user must pick a campaign before saving.
const EMPTY_REMINDER = {
  name: "",
  channel: "texts",
  message: "",
  triggerType: "lapse",
  lapseAmount: 24,
  lapseUnit: "hours",
  specificAt: "",
  timezone: "America/Los_Angeles",
  campaignId: "",
  dpdBuckets: [],
  isEnabled: true,
  validFrom: "",
  validTo: "",
};

// Human-readable trigger summary for the list row.
const triggerLabel = (r) => {
  if (r.TriggerType === "lapse") {
    return `${r.LapseAmount} ${r.LapseUnit} after outbound`;
  }
  if (r.TriggerType === "specific") {
    const tz = (TIMEZONE_OPTIONS.find((t) => t.value === r.Timezone) || {}).label || r.Timezone;
    const when = r.SpecificAt ? new Date(r.SpecificAt).toLocaleString() : "-";
    return `${when} (${tz})`;
  }
  return "-";
};

const dpdLabel = (buckets) =>
  !buckets || buckets.length === 0 ? "Any" : buckets.join(", ");

// ── Campaign send-time preview helpers ───────────────────────────────────────

// Format a UTC Date into a given IANA zone as "Mon, Jan 5, 2:30 PM".
const fmtZone = (date, timeZone) => {
  if (!date) return "—";
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone,
      weekday: "short", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit", hour12: true,
    }).format(date);
  } catch {
    return "—";
  }
};

// Hour-of-day (0-23) of a UTC instant in a given zone — used for quiet-hours.
const hourInZone = (date, timeZone) => {
  try {
    const h = new Intl.DateTimeFormat("en-US", {
      timeZone, hour: "numeric", hour12: false,
    }).formatToParts(date).find((p) => p.type === "hour")?.value;
    let n = Number(h);
    if (n === 24) n = 0;
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
};

// True if the instant is inside 8am-6pm Pacific.
const inQuietWindowPacific = (date) => {
  const h = hourInZone(date, "America/Los_Angeles");
  if (h == null) return false;
  return h >= QUIET_START_HR && h < QUIET_END_HR;
};

// Lapse -> milliseconds (mirrors lapseMs in processreminders.js).
const lapseToMs = (amount, unit) => {
  const n = Number(amount) || 0;
  if (unit === "days") return n * 24 * 60 * 60 * 1000;
  if (unit === "hours") return n * 60 * 60 * 1000;
  return 0;
};

// One preview row: an initial message time + (for lapse triggers) the projected
// reminder time, shown red when that projection falls outside quiet hours.
function CampaignTimeRow({ label, utcIso, triggerType, lapseAmount, lapseUnit }) {
  const initial = utcIso ? new Date(utcIso) : null;

  let projected = null;
  let projectedOutsideQuiet = false;
  if (initial && triggerType === "lapse") {
    const ms = lapseToMs(lapseAmount, lapseUnit);
    if (ms > 0) {
      projected = new Date(initial.getTime() + ms);
      projectedOutsideQuiet = !inQuietWindowPacific(projected);
    }
  }

  return (
    <Box sx={{ mb: 1 }}>
      <Typography sx={{ fontWeight: 600, fontSize: "0.8rem" }}>{label}</Typography>
      <Typography sx={{ fontSize: "0.78rem" }}>
        PST: {fmtZone(initial, "America/Los_Angeles")} &nbsp;|&nbsp;
        EST: {fmtZone(initial, "America/New_York")}
      </Typography>
      {triggerType === "lapse" && projected && (
        <Typography
          sx={{
            fontSize: "0.78rem",
            color: projectedOutsideQuiet ? "#d32f2f" : "text.primary",
            fontWeight: projectedOutsideQuiet ? 600 : 400,
          }}
        >
          Reminder would send — PST: {fmtZone(projected, "America/Los_Angeles")} | EST:{" "}
          {fmtZone(projected, "America/New_York")}
          {projectedOutsideQuiet ? "  ⚠ outside 8am–6pm Pacific" : ""}
        </Typography>
      )}
    </Box>
  );
}

function Reminders() {
  const [reminders, setReminders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = create
  const [form, setForm] = useState(EMPTY_REMINDER);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [campaigns, setCampaigns] = useState([]);

  // List filters — match the campaign pages: Category defaults to today, Language to All.
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState(todayCategory);
  const [languageFilter, setLanguageFilter] = useState("All");

  // Campaign send-time preview state.
  const [campTimes, setCampTimes] = useState(null); // { firstInitialUtc, lastInitialUtc, activeCount }
  const [campTimesLoading, setCampTimesLoading] = useState(false);
  const [campTimesError, setCampTimesError] = useState("");

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await fetchReminders();
      setReminders(data || []);
    } catch (e) {
      // service already logs
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load campaigns once for the single-select dropdown (campaign is required).
  useEffect(() => {
    (async () => {
      try {
        const data = await getcampaigns();
        setCampaigns(Array.isArray(data) ? data : []);
      } catch (e) {
        // non-fatal: dropdown will be empty until campaigns load
      }
    })();
  }, []);

  // Clear the preview whenever campaign or channel changes (stale otherwise).
  useEffect(() => {
    setCampTimes(null);
    setCampTimesError("");
  }, [form.campaignId, form.channel]);

  // Reset to first page whenever a list filter changes.
  useEffect(() => {
    setPage(0);
  }, [searchTerm, categoryFilter, languageFilter]);

  // Resolve a campaign id to its name for the list row.
  // Legacy null-campaign rows (created before campaign was required) surface as
  // a warning so they can be edited and assigned a campaign.
  const campaignName = (id) => {
    if (id == null || id === "") return "⚠ All campaigns (legacy)";
    const c = campaigns.find((x) => String(x.id) === String(id));
    return c ? c.name : `#${id}`;
  };

  // Reminders filtered by the Category/Language of their linked campaign.
  // A reminder matches when its campaign's Category/Language match the filters.
  // Legacy null-campaign reminders have no campaign attributes, so they only
  // appear when both filters are "All".
  const filteredReminders = reminders.filter((r) => {
    const term = searchTerm.trim().toLowerCase();
    const matchesSearch =
      !term ||
      r.Name?.toLowerCase().includes(term) ||
      r.Message?.toLowerCase().includes(term) ||
      campaignName(r.CampaignId)?.toLowerCase().includes(term);
    if (!matchesSearch) return false;
    if (categoryFilter === "All" && languageFilter === "All") return true;
    const c = campaigns.find((x) => String(x.id) === String(r.CampaignId));
    const catOk = categoryFilter === "All" || c?.Category === categoryFilter;
    const langOk = languageFilter === "All" || c?.Language === languageFilter;
    return catOk && langOk;
  });

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_REMINDER);
    setFormError("");
    setCampTimes(null);
    setCampTimesError("");
    setDialogOpen(true);
  };

  const openEdit = (r) => {
    setEditingId(r.Id);
    setForm({
      name: r.Name ?? "",
      channel: r.Channel ?? "texts",
      message: r.Message ?? "",
      triggerType: r.TriggerType ?? "lapse",
      lapseAmount: r.LapseAmount ?? 24,
      lapseUnit: r.LapseUnit ?? "hours",
      // datetime-local wants "YYYY-MM-DDTHH:mm"
      specificAt: r.SpecificAt ? String(r.SpecificAt).slice(0, 16) : "",
      timezone: r.Timezone ?? "America/Los_Angeles",
      // Legacy null-campaign reminders load with an empty campaign field; the
      // required validation then forces the editor to assign one before saving.
      campaignId: r.CampaignId ?? "",
      dpdBuckets: r.dpdBuckets ?? [],
      isEnabled: !!r.IsEnabled,
      validFrom: r.ValidFrom ? String(r.ValidFrom).slice(0, 10) : "",
      validTo: r.ValidTo ? String(r.ValidTo).slice(0, 10) : "",
    });
    setFormError("");
    setCampTimes(null);
    setCampTimesError("");
    setDialogOpen(true);
  };

  const setField = (key) => (e) => {
    const value = e?.target?.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const setBuckets = (e) => {
    const value = e.target.value;
    setForm((prev) => ({
      ...prev,
      dpdBuckets: typeof value === "string" ? value.split(",") : value,
    }));
  };

  // Fetch the campaign's first/last initial-message times for no-reply active
  // conversations on the selected channel. Clicking again refetches.
  const checkCampaignTimes = async () => {
    if (!form.campaignId) {
      setCampTimesError("Select a campaign first.");
      return;
    }
    setCampTimesLoading(true);
    setCampTimesError("");
    try {
      const { data } = await apiClient.get("/reminderCampaignTimes", {
        params: { campaignId: parseInt(form.campaignId, 10), channel: form.channel },
      });
      setCampTimes(data);
    } catch (e) {
      setCampTimesError("Could not fetch campaign times.");
    } finally {
      setCampTimesLoading(false);
    }
  };

  const validateForm = () => {
    if (!form.name.trim()) return "Name is required";
    if (!form.message.trim()) return "Message is required";
    // Campaign is required — reminders must be campaign-specific so a customer
    // enrolled in multiple campaigns is only reminded for the named campaign.
    if (form.campaignId === "" || form.campaignId == null) return "Campaign is required";
    if (form.triggerType === "lapse") {
      const n = parseInt(form.lapseAmount, 10);
      if (!Number.isFinite(n) || n <= 0) return "Lapse amount must be a positive number";
    } else {
      if (!form.specificAt) return "Specific date & time is required";
      if (!form.timezone) return "Timezone is required";
    }
    return "";
  };

  const buildPayload = () => {
    const base = {
      name: form.name.trim(),
      channel: form.channel,
      message: form.message,
      triggerType: form.triggerType,
      // Always a real campaign id — validateForm guarantees it is set.
      campaignId: parseInt(form.campaignId, 10),
      dpdBuckets: form.dpdBuckets,
      isEnabled: !!form.isEnabled,
      validFrom: form.validFrom || null,
      validTo: form.validTo || null,
    };
    if (form.triggerType === "lapse") {
      base.lapseAmount = parseInt(form.lapseAmount, 10);
      base.lapseUnit = form.lapseUnit;
    } else {
      base.specificAt = form.specificAt;
      base.timezone = form.timezone;
    }
    return base;
  };

  const handleSave = async () => {
    const err = validateForm();
    if (err) {
      setFormError(err);
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      const payload = buildPayload();
      if (editingId == null) await createReminder(payload);
      else await updateReminder(editingId, payload);
      setDialogOpen(false);
      await load();
    } catch (e) {
      setFormError(
        e?.response?.data?.error || "Failed to save reminder. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete reminder "${r.Name}"? This cannot be undone.`)) return;
    try {
      await deleteReminder(r.Id);
      setReminders((prev) => prev.filter((x) => x.Id !== r.Id));
    } catch (e) {
      load();
    }
  };

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 2, flexWrap: "wrap" }}>
        <TextField
          label="Search Reminders"
          variant="outlined"
          size={SIZES.SMALL}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ flexGrow: 1, minWidth: 220 }}
        />
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            select
            label="Category"
            size={SIZES.SMALL}
            sx={{ minWidth: 150 }}
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <MenuItem value="All">All</MenuItem>
            {CATEGORY_OPTIONS.map((d) => (
              <MenuItem key={d} value={d}>{d}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Language"
            size={SIZES.SMALL}
            sx={{ minWidth: 140 }}
            value={languageFilter}
            onChange={(e) => setLanguageFilter(e.target.value)}
          >
            <MenuItem value="All">All</MenuItem>
            {LANGUAGE_OPTIONS.map((l) => (
              <MenuItem key={l} value={l}>{l}</MenuItem>
            ))}
          </TextField>
          <Button variant="contained" size="small" onClick={openCreate}>
            New Reminder
          </Button>
        </Stack>
      </Box>

      <TableContainer sx={{ overflowX: "auto", "& .MuiTableCell-root": { whiteSpace: "nowrap" } }}>
        <Table size={SIZES.SMALL}>
          <TableHead>
            <TableRow>
              {REMINDER_COLUMNS.map((c) => (
                <TableCell key={c.id} align={c.id === "Actions" ? ALIGN.CENTER : ALIGN.LEFT}>
                  {c.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={REMINDER_COLUMNS.length} align={ALIGN.CENTER}>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            ) : filteredReminders.length > 0 ? (
              filteredReminders
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((r) => (
                <TableRow hover key={r.Id}>
                  <TableCell>{r.Name}</TableCell>
                  <TableCell>
                    <Chip
                      size={SIZES.SMALL}
                      label={r.Channel === "emails" ? "Emails" : "Texts"}
                      sx={{ fontWeight: 600, fontSize: "0.72rem" }}
                    />
                  </TableCell>
                  <TableCell>{triggerLabel(r)}</TableCell>
                  <TableCell>{dpdLabel(r.dpdBuckets)}</TableCell>
                  <TableCell>{campaignName(r.CampaignId)}</TableCell>
                  <TableCell>
                    <Switch
                      size={SIZES.SMALL}
                      checked={!!r.IsEnabled}
                      readOnly
                      sx={{ pointerEvents: "none" }}
                      inputProps={{ "aria-label": "enabled (read-only)", tabIndex: -1 }}
                    />
                  </TableCell>
                  <TableCell align={ALIGN.CENTER}>
                    <IconButton size={SIZES.SMALL} onClick={() => openEdit(r)} aria-label="edit">
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size={SIZES.SMALL} onClick={() => handleDelete(r)} aria-label="delete">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={REMINDER_COLUMNS.length} align={ALIGN.CENTER}>
                  No reminders configured.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          page={page}
          rowsPerPage={rowsPerPage}
          component={HTML_TAGS.DIV}
          count={filteredReminders.length}
          rowsPerPageOptions={[25, 50, 100]}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setPage(0);
            setRowsPerPage(parseInt(event.target.value, 10));
          }}
        />
      </TableContainer>

      {/* ── Create / Edit dialog ─────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId == null ? "New Reminder" : "Edit Reminder"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              size="small"
              fullWidth
              value={form.name}
              onChange={setField("name")}
            />

            <TextField
              select
              label="Channel"
              size="small"
              fullWidth
              value={form.channel}
              onChange={setField("channel")}
            >
              {CHANNEL_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>

            <TextField
              label="Message"
              size="small"
              fullWidth
              multiline
              minRows={8}
              maxRows={16}
              value={form.message}
              onChange={setField("message")}
              helperText="Variables: #firstname #lastname #dayspastdue #totalamountdue #monthlypayment #rollamt #extamt #exteligible #repoamt #repodate #cclast4digits #vehicleownamt #customerphonenumber"
            />

            <TextField
              select
              label="Trigger"
              size="small"
              fullWidth
              value={form.triggerType}
              onChange={setField("triggerType")}
            >
              {TRIGGER_TYPE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>

            {/* Conditional trigger fields */}
            {form.triggerType === "lapse" ? (
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Lapse amount"
                  type="number"
                  size="small"
                  fullWidth
                  value={form.lapseAmount}
                  onChange={setField("lapseAmount")}
                  inputProps={{ min: 1 }}
                />
                <TextField
                  select
                  label="Unit"
                  size="small"
                  fullWidth
                  value={form.lapseUnit}
                  onChange={setField("lapseUnit")}
                >
                  {LAPSE_UNIT_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </TextField>
              </Stack>
            ) : (
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Send at"
                  type="datetime-local"
                  size="small"
                  fullWidth
                  value={form.specificAt}
                  onChange={setField("specificAt")}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  select
                  label="Timezone"
                  size="small"
                  fullWidth
                  value={form.timezone}
                  onChange={setField("timezone")}
                >
                  {TIMEZONE_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </TextField>
              </Stack>
            )}

            {/* DPD multi-select. No selection == Any. */}
            <FormControl size="small" fullWidth>
              <InputLabel id="dpd-label">DPD buckets (none = Any)</InputLabel>
              <Select
                labelId="dpd-label"
                multiple
                value={form.dpdBuckets}
                onChange={setBuckets}
                input={<OutlinedInput label="DPD buckets (none = Any)" />}
                renderValue={(selected) =>
                  selected.length === 0 ? "Any" : selected.join(", ")
                }
              >
                {DPD_BUCKET_OPTIONS.map((b) => (
                  <MenuItem key={b} value={b}>
                    <Checkbox checked={form.dpdBuckets.indexOf(b) > -1} />
                    <ListItemText primary={b} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Campaign is REQUIRED — no "Any" option. A reminder must name a
                campaign so it only fires against that campaign's customerdata
                row, never across other campaigns the customer is enrolled in. */}
            <TextField
              select
              required
              label="Campaign"
              size="small"
              fullWidth
              value={form.campaignId === null ? "" : form.campaignId}
              onChange={setField("campaignId")}
              helperText="Required — the reminder applies only to this campaign."
            >
              {campaigns.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </TextField>

            {/* Campaign send-time preview — first/last initial message for
                no-reply active conversations, in PST & EST, with the projected
                reminder time (initial + lapse) shown red when outside quiet hours. */}
            <Box>
              <Button
                variant="outlined"
                size="small"
                onClick={checkCampaignTimes}
                disabled={!form.campaignId || campTimesLoading}
              >
                {campTimesLoading ? "Checking…" : "Check campaign send times"}
              </Button>

              {campTimesError && (
                <Alert severity="warning" sx={{ mt: 1 }}>{campTimesError}</Alert>
              )}

              {campTimes && (
                <Box sx={{ mt: 1, p: 1.5, border: "1px solid #e0e0e0", borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ display: "block", mb: 1 }}>
                    {campTimes.activeCount} no-reply active {form.channel} conversation(s) in this campaign.
                  </Typography>

                  {campTimes.firstInitialUtc || campTimes.lastInitialUtc ? (
                    <>
                      <CampaignTimeRow
                        label="First initial message"
                        utcIso={campTimes.firstInitialUtc}
                        triggerType={form.triggerType}
                        lapseAmount={form.lapseAmount}
                        lapseUnit={form.lapseUnit}
                      />
                      <CampaignTimeRow
                        label="Last initial message"
                        utcIso={campTimes.lastInitialUtc}
                        triggerType={form.triggerType}
                        lapseAmount={form.lapseAmount}
                        lapseUnit={form.lapseUnit}
                      />
                      {form.triggerType === "specific" && (
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          Specific-time trigger — reminder sends at the configured time, not relative to these.
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Typography variant="caption">
                      No initial messages found for this campaign/channel yet.
                    </Typography>
                  )}
                </Box>
              )}
            </Box>

            <FormControlLabel
              control={
                <Switch checked={!!form.isEnabled} onChange={setField("isEnabled")} />
              }
              label="Enabled"
            />

            {formError ? (
              <Typography sx={{ color: "#d32f2f", fontSize: "0.8rem" }}>{formError}</Typography>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Reminders;